import { FileTextOutlined, ToolOutlined } from "@ant-design/icons";
import { Col, Row } from "antd";
import React, { useMemo } from "react";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import { formatDisplayDateOnly, ROUTES } from "~/utils";
import type { HomePageLoaderData } from "../loaders/HomePageLoader";
import { getLeaseEndUrgency } from "../utils/leaseEndUrgency";
import { HomeActionCard } from "./HomeActionCard";
import { HomeFinanceCard } from "./HomeFinanceCard";
import { HomeNextSteps } from "./HomeNextSteps";
import { LocationSearchBar } from "~/components/ListingBrowse";

const pageStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 24,
  width: "100%",
  marginTop: 8,
};

export function TenantHomeView({
  inProgressMaintenance,
  leaseEndDate,
  pendingLeases,
}: HomePageLoaderData) {
  const { t, i18n } = useAppTranslation();

  const maintenanceDescription = useMemo(() => {
    if (inProgressMaintenance.length === 0) {
      return t("Home_maintenance_card_description");
    }
    if (inProgressMaintenance.length === 1) {
      return t("Home_maintenance_in_progress_one", {
        title: inProgressMaintenance[0].title,
      });
    }
    return t("Home_maintenance_in_progress_many", {
      count: inProgressMaintenance.length,
      title: inProgressMaintenance[0].title,
    });
  }, [inProgressMaintenance, t]);

  const leaseUrgency = getLeaseEndUrgency(leaseEndDate);
  const leaseDescription = useMemo(() => {
    if (!leaseEndDate || !leaseUrgency) {
      return t("Home_view_lease_card_description");
    }
    const formatted = formatDisplayDateOnly(leaseEndDate, i18n.language);
    if (leaseUrgency === "danger") {
      return t("Home_lease_ends_soon", { date: formatted });
    }
    if (leaseUrgency === "warning") {
      return t("Home_lease_ends_upcoming", { date: formatted });
    }
    return t("Home_lease_ends_on", { date: formatted });
  }, [leaseEndDate, leaseUrgency, t, i18n.language]);

  return (
    <div style={pageStyle}>
      <HomeFinanceCard
        title={t("Balance_Due")}
        description={t("View_Details")}
        descriptionHref={ROUTES.paymentTablePage}
        buttonText={t("Make_Payment")}
        buttonHref={ROUTES.paymentsPage}
      />

      <HomeNextSteps pendingLeases={pendingLeases} />

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <HomeActionCard
            to={ROUTES.serviceRequestTablePage}
            icon={<ToolOutlined />}
            title={t("Home_maintenance_link")}
            description={maintenanceDescription}
            urgency={
              inProgressMaintenance.length > 0 ? "warning" : "default"
            }
          />
        </Col>
        <Col xs={24} md={12}>
          <HomeActionCard
            to={ROUTES.lease}
            icon={<FileTextOutlined />}
            title={t("Home_view_lease_link")}
            description={leaseDescription}
            urgency={leaseUrgency ?? "default"}
          />
        </Col>
      </Row>

      <LocationSearchBar
        title={t("Home_find_new_home_title")}
        subtitle={t("Home_search_section_subtitle")}
      />
    </div>
  );
}
